<template>
    <div class="modal-backdrop" @click.self="handleClose">
        <div class="modal-card">
            <button class="modal-close" type="button" @click="handleClose">&times;</button>

            <div class="modal-content">
                <div class="lock-icon-large">
                    <span class="lock-icon-mask" aria-label="Locked"></span>
                </div>

                <div class="modal-header">
                    <h2 class="modal-title">{{ itemName }}</h2>
                    <p class="modal-subtitle">{{ itemType === 'benefit' ? 'Member Benefit' : 'Voucher Offer' }}</p>
                </div>

                <div class="tier-requirement">
                    <div class="current-tier">
                        <span class="tier-label">Current tier</span>
                        <div class="tier-badge current">
                            <img v-if="currentTierIcon" :src="currentTierIcon" alt="Current tier" />
                            <span>{{ displayCurrentTierName }}</span>
                        </div>
                    </div>

                    <div class="upgrade-arrow">
                        <img src="/img/member-icon/arrow-right.svg" alt="Upgrade to" />
                    </div>

                    <div class="required-tier">
                        <span class="tier-label">Required tier</span>
                        <div class="tier-badge required">
                            <img v-if="requiredTierIcon" :src="requiredTierIcon" alt="Required tier" />
                            <span>{{ displayRequiredTierName }}</span>
                        </div>
                    </div>
                </div>

                <div class="upgrade-info">
                    <p class="upgrade-text">
                        Upgrade to <strong>{{ displayRequiredTierName }}</strong> tier to unlock this {{ itemType === 'benefit' ? 'benefit' : 'voucher' }} and many more exclusive rewards.
                    </p>
                    
                    <div class="upgrade-methods">
                        <h4 class="methods-title">How to upgrade your tier:</h4>
                        <div class="method-list">
                            <div class="method-item">
                                <div class="method-icon">
                                    <i class="fas fa-bed"></i>
                                </div>
                                <div class="method-text">
                                    <span class="method-name">Book hotel rooms</span>
                                    <span class="method-desc">Make reservations through our booking engine</span>
                                </div>
                            </div>
                            <div class="method-item">
                                <div class="method-icon">
                                    <i class="fas fa-shopping-cart"></i>
                                </div>
                                <div class="method-text">
                                    <span class="method-name">Make transactions</span>
                                    <span class="method-desc">Shop at any of our partner merchants</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn-secondary" @click="handleClose">
                        Maybe Later
                    </button>
                    <button type="button" class="btn-primary" @click="viewTierBenefits">
                        View Tier Benefits
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'TierUpgradeModal',
    props: {
        itemName: {
            type: String,
            required: true
        },
        itemType: {
            type: String,
            default: 'voucher'
        },
        requiredTierName: {
            type: String,
            required: true
        },
        requiredTierIcon: {
            type: String,
            default: ''
        },
        currentTierName: {
            type: String,
            required: true
        },
        currentTierIcon: {
            type: String,
            default: ''
        }
    },
    emits: ['close', 'view-tier-benefits'],
    mounted() {
        document.body.style.overflow = 'hidden';
    },
    beforeUnmount() {
        document.body.style.overflow = '';
    },
    computed: {
        displayCurrentTierName() {
            const name = this.currentTierName;
            const finalName = name && name !== '-' ? name : 'Member';
            return finalName.length > 25 ? finalName.substring(0, 25) : finalName;
        },
        displayRequiredTierName() {
            const name = this.requiredTierName;
            const finalName = name && name !== '-' ? name : 'Member';
            return finalName.length > 25 ? finalName.substring(0, 25) : finalName;
        }
    },
    methods: {
        handleClose() {
            this.$emit('close');
        },
        viewTierBenefits() {
            this.$emit('view-tier-benefits');
        }
    }
}
</script>

<style scoped>
.modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.55);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px;
    z-index: 1000;
}

.modal-card {
    position: relative;
    width: min(480px, 100%);
    background: #ffffff;
    border-radius: 16px;
    padding: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
}

.modal-close {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid #E1E4EA;
    background: rgba(255, 255, 255, 0.92);
    color: #111827;
    font-size: 18px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
}

.modal-close:hover {
    background: #F3F4F6;
}

.modal-content {
    padding: 32px 24px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    text-align: center;
}

.lock-icon-large {
    width: 64px;
    height: 64px;
    background: var(--secondary-color, rgba(249, 62, 62, 0.1));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.lock-icon-mask {
    display: inline-block;
    width: 32px;
    height: 32px;
    background-color: var(--primary-color, #F93E3E);
    -webkit-mask-image: url('/img/member-icon/locked-icon.svg');
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-image: url('/img/member-icon/locked-icon.svg');
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    opacity: 0.8;
}

.modal-header {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.modal-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #1B1E28;
    line-height: 1.3;
}

.modal-subtitle {
    margin: 0;
    font-size: 14px;
    color: #717784;
    line-height: 1.4;
}

.tier-requirement {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    justify-content: center;
}

.current-tier,
.required-tier {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.tier-label {
    font-size: 12px;
    color: #717784;
    font-weight: 500;
}

.tier-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
}

.tier-badge.current {
    background: rgba(113, 119, 132, 0.1);
    color: #717784;
    border: 1px solid #E1E4EA;
}

.tier-badge.required {
    background: var(--secondary-color, rgba(249, 62, 62, 0.1));
    color: var(--primary-color, #F93E3E);
    border: 1px solid var(--primary-border, rgba(249, 62, 62, 0.3));
}

.tier-badge img {
    width: 20px;
    height: 20px;
    object-fit: contain;
}

.upgrade-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.6;
}

.upgrade-arrow img {
    width: 20px;
    height: 20px;
    opacity: 0.6;
}

.upgrade-info {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.upgrade-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: #374151;
}

.upgrade-methods {
    background: #F8FAFC;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #E2E8F0;
}

.methods-title {
    margin: 0 0 12px 0;
    font-size: 13px;
    font-weight: 600;
    color: #1E293B;
    text-align: left; /* Ensure title is left aligned */
}

.method-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.method-item {
    display: flex;
    align-items: center;
    gap: 12px;
}

.method-icon {
    width: 32px;
    height: 32px;
    background: var(--primary-color, #F93E3E);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.method-icon img {
    color: white;
    font-size: 14px;
}

.method-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: left; /* Ensure text is left aligned */
}

.method-name {
    font-size: 13px;
    font-weight: 600;
    color: #1E293B;
    line-height: 1.2;
}

.method-desc {
    font-size: 11px;
    color: #64748B;
    line-height: 1.3;
}

.modal-actions {
    display: flex;
    gap: 12px;
    width: 100%;
}

.btn-secondary,
.btn-primary {
    flex: 1;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
}

.btn-secondary {
    background: #F5F6FA;
    color: #717784;
}

.btn-secondary:hover {
    background: #E1E4EA;
}

.btn-primary {
    background: var(--primary-color, #F93E3E);
    color: white;
}

.btn-primary:hover {
    opacity: 0.9;
}

@media (max-width: 480px) {
    /* Don't stack tier requirement on mobile to save vertical space if possible, or keep as is but check height */
    .tier-requirement {
        gap: 12px;
    }

    /* Better mobile responsiveness */
    .modal-card {
        width: 100%;
        max-width: none;
        margin: 16px; /* Add margin so it doesn't touch edges */
        border-radius: 16px;
        /* Prevent full height but allow scrolling if needed */
        max-height: 90vh;
        overflow-y: auto;
    }

    .modal-content {
        padding: 24px 20px;
    }
}

.method-icon i {
    color: white;
    font-size: 14px;
}
</style>